import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { InfoIcon, ResendInvitationIcon } from '../../../components/Icon';
import Tooltip from '../../../components/Tooltip';
import RosterActionsCell, { PendingRemoval } from '../components/RosterActionsCell';
import type { RosterEntry } from '../types';

const vettingProgressMap: Record<string, string> = {
  ACTIVE: 'Active',
  ENROLLMENT_IN_PROGRESS: 'Enrollment In Progress',
  INVITE_ACCEPTED: 'Invite Accepted',
  INVITE_EXPIRED: 'Invite Expired',
  INVITE_SENT: 'Invite Sent',
  INVITED: 'Invited',
  REMOVED: 'Removed',
  SUSPENDED: 'Suspended',
};

interface UseRosterColumnsOptions {
  canManageRoster?: boolean;
  isCor?: boolean;
  isResendInvitationPending?: boolean;
  onMoveUser?: (entry: RosterEntry) => void;
  onRemoveUser?: (entry: RosterEntry) => void;
  onResendInvitation?: (entry: RosterEntry) => void;
  onReviewApplication?: (applicationId: string) => void;
  pendingApprovals?: Map<string, string>;
  pendingRemovals?: Map<string, PendingRemoval>;
  onCancelRemoval?: (entry: RosterEntry) => void;
}

export function useRosterColumns({
  canManageRoster,
  isCor,
  isResendInvitationPending,
  onMoveUser,
  onRemoveUser,
  onResendInvitation,
  onReviewApplication,
  pendingApprovals,
  pendingRemovals,
  onCancelRemoval,
}: UseRosterColumnsOptions = {}) {
  return useMemo<ColumnDef<RosterEntry>[]>(
    () => [
      {
        accessorKey: 'legalLastName',
        enableSorting: true,
        header: 'Legal Last Name',
        size: 192,
      },
      {
        accessorKey: 'legalFirstName',
        enableSorting: true,
        header: 'Legal First Name',
        size: 192,
      },
      {
        accessorKey: 'laborCategory',
        enableSorting: true,
        header: 'Labor Category',
        size: 232,
      },
      {
        accessorKey: 'vettingProgress',
        cell: ({ row }) => {
          const entry = row.original;
          const isInviteExpired = entry.vettingProgress === 'INVITE_EXPIRED';
          const userName = `${entry.legalFirstName} ${entry.legalLastName}`.trim();
          const applicationId = pendingApprovals?.get(entry.userId);

          return (
            <div className='roster-vetting-progress-cell'>
              <span>
                {isInviteExpired
                  ? 'Invite Expired'
                  : vettingProgressMap[entry.vettingProgress] || entry.vettingProgress}
              </span>
              {canManageRoster && isInviteExpired && (
                <button
                  aria-label={`Resend invitation to ${userName || entry.email}`}
                  className='roster-vetting-progress-cell__resend-button action-icon--blue-round'
                  disabled={isResendInvitationPending}
                  title='Resend invitation'
                  type='button'
                  onClick={() => onResendInvitation?.(entry)}>
                  <ResendInvitationIcon size={24} />
                </button>
              )}
              {isCor && applicationId && (
                <button
                  aria-label={`Review application for ${userName || entry.email}`}
                  className='roster-vetting-progress-cell__review-button'
                  title='Review Application'
                  type='button'
                  onClick={() => onReviewApplication?.(applicationId)}>
                  Review
                </button>
              )}
            </div>
          );
        },
        enableSorting: false,
        header: () => (
          <div style={{ alignItems: 'center', display: 'flex', gap: '0.5rem' }}>
            <span>Vetting Progress</span>
            <Tooltip
              placement='top'
              title='Vetting Progress indicates the current status of the background check and security clearance process for each user. Statuses include: Cleared, In Progress, Fingerprint Complete (Pending eAPP), PIV Package Pending Review, Background Check Complete, Awaiting Final Approval, Security Clearance Granted, or Not Vetted.'>
              <InfoIcon />
            </Tooltip>
          </div>
        ),
        size: 200,
      },
      {
        cell: ({ row }) => (
          <RosterActionsCell
            canManageRoster={canManageRoster}
            pendingRemoval={pendingRemovals?.get(row.original.userId)}
            row={row}
            onCancelRemoval={onCancelRemoval}
            onMove={onMoveUser}
            onRemove={onRemoveUser}
          />
        ),
        enableSorting: false,
        header: 'Actions',
        id: 'actions',
        size: 184,
      },
    ],
    [
      isResendInvitationPending,
      canManageRoster,
      isCor,
      onMoveUser,
      onRemoveUser,
      onResendInvitation,
      onReviewApplication,
      pendingApprovals,
      pendingRemovals,
      onCancelRemoval,
    ],
  );
}
